import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError } from 'rxjs';

import { SessionService } from '@galaxy/core';
import { catchError, map, switchMap } from 'rxjs/operators';

export class ApiError implements Error {
  name: string;
  message: string;
  code: number;
  data?: any;

  constructor(message: string, code: number, data?: any) {
    this.name = `ApiError${code}`;
    this.message = message;
    this.code = code;
    this.data = data;
  }
}

@Injectable({ providedIn: 'root' })
export class VApiService {
  constructor(
    private http: HttpClient,
    private sessionService: SessionService,
  ) {}

  public get(url: string, options?: any): Observable<any> {
    return this.http
      .get(url, Object.assign({}, this.apiOptions, options))
      .pipe(map(this.extractData), catchError(this.handleError));
  }

  public put(url: string, body: any, options?: any): Observable<any> {
    return this.http
      .put(url, body, Object.assign({}, this.apiOptions, options))
      .pipe(map(this.extractData), catchError(this.handleError));
  }

  public post(url: string, body: any, options?: any): Observable<any> {
    return this.sessionService.getSessionId().pipe(
      switchMap(() => {
        return this.http.post(url, body, Object.assign({}, { withCredentials: true }, options));
      }),
      map(this.extractData),
      catchError(this.handleError),
    );
  }

  public generateUrlWithParams(url: string, params: any): string {
    if (Object.keys(params).length === 0) {
      return url;
    }

    const finalUrl = url + '?';
    const finalParams = Object.keys(params)
      .map((param_key) => `${param_key}=${params[param_key]}`)
      .join('&');
    return finalUrl + finalParams;
  }

  public generateRepeatedUrlParams(paramName: string, params: string[]): string {
    let repeatedParams = '';
    params.forEach((id, index) => {
      if (index !== 0) {
        repeatedParams += '&';
      }
      repeatedParams += paramName + '=' + id;
    });
    return repeatedParams;
  }

  private apiOptions(sessionId: string): any {
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${sessionId}`,
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
      responseType: 'json',
    };
  }

  private extractData(res: any): any {
    try {
      return Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
    } catch (error) {
      return res;
    }
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    try {
      const errorInfo = error;
      const err = new ApiError(errorInfo.message, errorInfo.status, errorInfo.error);
      return observableThrowError(err);
    } catch (error) {
      const err = new ApiError('Unknown Error', 500);
      return observableThrowError(err);
    }
  }
}
